import { Box, Card, CircularProgress, Typography } from '@mui/material';
import type { CardProps } from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';
import { Iconify } from 'src/components/iconify';
import { ColorType } from 'src/theme/core';

export type ItemCardProps = CardProps & {
  icon: string;
  title: string;
  total: string;
  totalTooltip?: string;
  isValueLoading?: boolean;
  color?: ColorType;
  tooltip?: string;
};

export const ItemCard = ({
  sx,
  icon,
  title,
  total,
  totalTooltip,
  isValueLoading = false,
  color = 'warning',
  tooltip,
  ...other
}: ItemCardProps) => {
  const theme = useTheme();

  return (
    <Card
      sx={{
        py: 3,
        pl: 3,
        pr: 3,
        mr: '5px',
        borderRadius: '16px',
        position: 'relative',
        ...sx,
      }}
      {...other}
    >
      <Box sx={{ flexGrow: 1 }}>
        <Stack flexDirection="row" alignItems="center">
          <Typography noWrap variant="h6">
            {title}
          </Typography>
          {tooltip && (
            <Tooltip
              title={<Typography fontSize={12}>{tooltip}</Typography>}
              placement="top"
              enterDelay={300}
            >
              <Iconify
                width={16}
                icon="eva:info-outline"
                ml="5px"
                sx={{ cursor: 'pointer', color: 'text.disabled' }}
              />
            </Tooltip>
          )}
        </Stack>
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={totalTooltip ?? total} placement="top" enterDelay={300}>
            <Typography
              variant="h6"
              sx={{
                width: 'fit-content',
                cursor: 'default',
                opacity: isValueLoading ? 0.72 : 1,
                transition: 'opacity 0.2s ease',
              }}
            >
              {total}
            </Typography>
          </Tooltip>
          {isValueLoading && <CircularProgress size={14} thickness={5} />}
        </Box>
      </Box>
      <Iconify
        icon={icon}
        sx={{
          top: 24,
          right: 16,
          width: 42,
          height: 42,
          position: 'absolute',
          color: theme.vars.palette[color].main,
        }}
      />
      <Box
        sx={{
          top: -44,
          width: 160,
          zIndex: 1,
          height: 160,
          right: -104,
          opacity: 0.12,
          borderRadius: 3,
          position: 'absolute',
          transform: 'rotate(40deg)',
          background: `linear-gradient(135deg, ${theme.vars.palette[color].main} 0%, ${theme.vars.palette[color].dark} 100%)`,
        }}
      />
    </Card>
  );
};
